import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';

import Button from 'components/library/inputs/Button';
import Flash from 'components/library/utils/Flash';
import InterviewTemplateListTable from './InterviewTemplateListTable';
import InterviewTemplateSelectInput from '../../../library/inputs/InterviewTemplateSelectInput';
import LoadingSpinner from 'components/library/utils/LoadingSpinner';
import Section from 'components/library/layout/Section';
import {
  StyledContainer,
  StyledFiltersContainer,
  StyledHeaderActions,
} from './styles';
import SelectBaseTemplateModal from 'components/library/data-display/SelectBaseTemplateModal';
import TextInput from 'components/library/inputs/TextInput';
import useSyncStateWithQuery from 'hooks/use-sync-state-with-query';
import { useInterviewTemplates } from 'hooks/queries/interview-templates';

import type { ChangeEvent } from 'react';
import { correctPath } from 'libraries/gem';

const InterviewTemplateList = () => {
  const [search, querySearch, setSearch] = useSyncStateWithQuery<string>('q', '', { delay: 200 });
  const [showArchived, , setShowArchived] = useSyncStateWithQuery<string>('show_archived', 'false');
  const [pageNumber, queryPageNumber, setPageNumber] = useSyncStateWithQuery<string>('page', '1');

  const [isSelectBaseTemplateModalOpen, setIsSelectBaseTemplateModalOpen] = useState<boolean>(false);

  const {
    data: interviewTemplates,
    error,
    isFetching,
  } = useInterviewTemplates({
    archived: showArchived === 'true',
    name: querySearch,
    limit: 10,
    offset: 10 * (parseInt(queryPageNumber, 10) - 1),
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (parseInt(pageNumber, 10) > 1) {
      setPageNumber('1', { method: 'replace' });
    }
  }, [pageNumber]);

  const handleShowArchivedChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setShowArchived(JSON.stringify(e.target.checked));
  }, []);

  const handlePageNumberChange = useCallback((value: number) => {
    setPageNumber(value.toString());
  }, []);

  return (
    <StyledContainer className="interview-template-list-container">
      <Helmet>
        <title>Interview Templates | Gem Scheduling</title>
      </Helmet>
      <Section
        headerActions={
          <StyledHeaderActions>
            <Button
              color="gem-outline"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setIsSelectBaseTemplateModalOpen(true)}
              value="New template from base"
            />
            <Link to={correctPath('/app/interview-templates/create')}>
              <Button
                color="gem-blue"
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                value="New template"
              />
            </Link>
          </StyledHeaderActions>
        }
        title="Interview templates"
      >
        <SelectBaseTemplateModal
          isOpen={isSelectBaseTemplateModalOpen}
          onToggle={() => setIsSelectBaseTemplateModalOpen(!isSelectBaseTemplateModalOpen)}
          redirectPath={correctPath('/app/interview-templates/create')}
          resourceName="interview template"
          selectInputComponent={InterviewTemplateSelectInput}
        />
        <StyledFiltersContainer>
          <TextInput
            id="text-input-interview-template-filter"
            isAutofocus
            label="Search"
            leftIcon={isFetching ? <LoadingSpinner /> : <FontAwesomeIcon icon={faSearch} />}
            onChange={handleSearchChange}
            placeholder="Interview template name"
            value={search}
          />
        </StyledFiltersContainer>
        <Flash
          message={error?.message}
          showFlash={Boolean(error)}
          type="danger"
        />
        {interviewTemplates && (
          <InterviewTemplateListTable
            interviewTemplates={interviewTemplates.interview_templates}
            onPageNumberChange={handlePageNumberChange}
            onShowArchivedChange={handleShowArchivedChange}
            pageNumber={parseInt(pageNumber, 10)}
            showArchived={showArchived === 'true'}
            totalCount={interviewTemplates.total}
          />
        )}
      </Section>
    </StyledContainer>
  );
};

export default InterviewTemplateList;
